import angular from 'angular'

import { and, makeOR, makeAND, makeXOR } from '../utils/bits'
import { wrap, spread, sample, stubTrue, isPlainObject } from 'lodash-es'

import envRaw from '../utils/env-raw'
import Emitter from '../utils/emitter'
import postFlag from '../utils/post-flag'
import postPosition from '../utils/post-position'
import entryEmitter from '../utils/entry-emitter'
import attachGetters from '../utils/attach-getters'
import attachSetters from '../utils/attach-setters'
import postViewIndex from '../utils/post-view-index'
import attachEmitters from '../utils/attach-emitters'

import Template from './gallery.component.ejs'

angular
  .module('ggs.gallery')
  .component('ggsGallery', {
    template: Template({
      LE: envRaw.liveEditMode
    }),
    controller: GalleryComponent,
    bindings: {
      ggsBootstrap: '<',
      ggsMainReadyCallback: '<'
    }
  })

function GalleryComponent(
  $q, qh, env, $log, $scope, imgQueue, notification, WaitOnLoad,
  StayInQueue, LazyResource, DefineMethod, DefineHandler,
  FormatPost, galleryRpc, EmbedManager, StateManager, ModalsManager,
  ReadyStateManager, PostListManager, OrderTypeManager, OverridesManager,
  AttributesManager, InstantModeManager, PresentationManager, GalleryStylerNew,
  GalleryStyleHandlers) {

  const sign = 'galleryMain'
  const $ctrl = $scope.$ctrl = attachGetters(attachSetters(this))

  $ctrl.wrapM = DefineMethod(sign)
  $ctrl.wrapH = DefineHandler(sign)

  const stayInPostQueue = StayInQueue(`${sign}::post`)
  const stayInListQueue = StayInQueue(`${sign}::list`)

  $ctrl.postFlagEmitter = Emitter(`${sign}::postFlag`)
  $ctrl.postVoteEmitter = Emitter(`${sign}::postVote`)

  $ctrl.headerHeight = undefined
  $ctrl.headerHeightEmitter = Emitter(`${sign}::headerHeight`)

  $ctrl.isFlowInViewport = undefined
  $ctrl.isFlowInViewportEmitter = Emitter(`${sign}::isFlowInViewport`)

  $ctrl.isButtonsLineTooShort = undefined
  $ctrl.isButtonsLineTooShortEmitter = Emitter(`${sign}::isButtonsLineTooShort`)

  $ctrl.isButtonsLineOverloaded = undefined
  $ctrl.isButtonsLineOverloadedEmitter = Emitter(`${sign}::isButtonsLineOverloaded`)

  $ctrl.hasOneButtonOnly = attachEmitters(
    $ctrl.ggsBootstrap.properties.key2emitter.allow_entry,
    $ctrl.ggsBootstrap.properties.key2emitter.allow_sorting,
    makeXOR(
      $ctrl.ggsBootstrap.properties.key2get.allow_entry,
      $ctrl.ggsBootstrap.properties.key2get.allow_sorting))

  $ctrl.showsButtonsInColumn = attachEmitters(
    $ctrl.hasOneButtonOnly,
    $ctrl.isButtonsLineOverloadedEmitter,
    $ctrl.isButtonsLineTooShortEmitter,
    makeOR(
      $ctrl.getters.isButtonsLineOverloaded,
      makeAND(
        $ctrl.hasOneButtonOnly,
        $ctrl.getters.isButtonsLineTooShort)))

  $ctrl.showsLoadMoreButton = () => true
    && $ctrl.ggsBootstrap.properties.show_load_more_button
    && postListManager.isPostListReady()
    && postListManager.isTopLimitReached()
    && (postListManager.isGlobalLimitReached()
        ? postListManager.hasPreloadedAbovePosts()
        : postListManager.hasPreloadedPosts())

  $ctrl.showsEmptyPostListReplacer = () => false
    || postListManager.zeroPosts()
    || postListManager.everyPostFail()

  // <ggs-gallery-preview-flow/>
  const extFlow = LazyResource(
    'flushPrependQueue',
    'flushAppendQueue',
    'showPreviewsDesc',
    'hidePreviewsDesc',
    'instantMode',
    'lazyMode',
    'scrollTo',
    'arrange',
    'resize',
    'remove')

  $ctrl.wrapH('flowAttachCallback', (
    extFlowFlushPrependQueue,
    extFlowFlushAppendQueue,
    extFlowShowPreviewsDesc,
    extFlowHidePreviewsDesc,
    extFlowInstantMode,
    extFlowLazyMode,
    extFlowScrollTo,
    extFlowArrange,
    extFlowResize,
    extFlowRemove) => {

    extFlow.flushPrependQueue.allow(extFlowFlushPrependQueue)
    extFlow.flushAppendQueue.allow(extFlowFlushAppendQueue)
    extFlow.showPreviewsDesc.allow(extFlowShowPreviewsDesc)
    extFlow.hidePreviewsDesc.allow(extFlowHidePreviewsDesc)
    extFlow.instantMode.allow(extFlowInstantMode)
    extFlow.lazyMode.allow(extFlowLazyMode)
    extFlow.scrollTo.allow(extFlowScrollTo)
    extFlow.arrange.allow(extFlowArrange)
    extFlow.resize.allow(extFlowResize)
    extFlow.remove.allow(extFlowRemove)
  })

  // <ggs-gallery-campaign/>
  const extCampaign = LazyResource(
    'close',
    'isOpen',
    'isClosed')

  $ctrl.wrapH('campaignAttachCallback', (
    extCampaignClose,
    extCampaignIsOpen,
    extCampaignIsClosed) => {

    extCampaign.close.replace(extCampaignClose)
    extCampaign.isOpen.replace(extCampaignIsOpen)
    extCampaign.isClosed.replace(extCampaignIsClosed)
  })

  // <ggs-gallery-modal-one/>
  const extOne = LazyResource(
    'open',
    'close',
    'isOpen',
    'isClosed')

  $ctrl.wrapH('oneAttachCallback', (
    extOneOpen,
    extOneClose,
    extOneIsOpen,
    extOneIsClosed) => {

    extOne.open.replace(extOneOpen)
    extOne.close.replace(extOneClose)
    extOne.isOpen.replace(extOneIsOpen)
    extOne.isClosed.replace(extOneIsClosed)
  })

  // <ggs-gallery-modal-list/>
  const extList = LazyResource(
    'prev',
    'next',
    'open',
    'close',
    'slide',
    'isOpen',
    'isClosed')

  $ctrl.wrapH('listAttachCallback', (
    extListPrev,
    extListNext,
    extListOpen,
    extListClose,
    extListSlide,
    extListIsOpen,
    extListIsClosed) => {

    extList.prev.replace(extListPrev)
    extList.next.replace(extListNext)
    extList.open.replace(extListOpen)
    extList.close.replace(extListClose)
    extList.slide.replace(extListSlide)
    extList.isOpen.replace(extListIsOpen)
    extList.isClosed.replace(extListIsClosed)
  })

  // internal methods
  $ctrl.wrapM('isAllModalsClosed',
    qh.makeFlow(
      qh.makeJoin(
        qh.makeOr($ctrl.ggsBootstrap.properties.key2get.negate.modals_use, extOne.isClosed),
        qh.makeOr($ctrl.ggsBootstrap.properties.key2get.negate.modals_use, extList.isClosed),
        qh.makeOr($ctrl.ggsBootstrap.properties.key2get.negate.allow_entry, extCampaign.isClosed)),
      qh.tap(wrap(`${sign}#isAllModalsClosed:`, $log.debug)),
      qh.wrap(spread(and))))

  $ctrl.wrapM('closeModals',
    stayInListQueue.make(
      qh.makeJoin(
        qh.makeIf(qh.makeAnd($ctrl.ggsBootstrap.properties.key2get.modals_use, extOne.isOpen), extOne.close),
        qh.makeIf(qh.makeAnd($ctrl.ggsBootstrap.properties.key2get.modals_use, extList.isOpen), extList.close),
        qh.makeIf(qh.makeAnd($ctrl.ggsBootstrap.properties.key2get.allow_entry, extCampaign.isOpen), extCampaign.close))))

  $ctrl.wrapM('openPost',
    stayInListQueue.make((post) => {
      if (!postListManager.contains(post)) return $log.warn(`${sign}#openPost: post is not in postList (probably order_type was changed) - skip!`)
      if (!$ctrl.ggsBootstrap.properties.modals_use) return $log.warn(`${sign}#openPost: "modals_use" is disabled - skip!`)

      return $ctrl.isAllModalsClosed().then((closed) => {
        if (!closed) return $log.warn(`${sign}#openPost: some modals are not closed - skip!`)
        return extList.open(post)
      })
    }))

  $ctrl.wrapM('openSamplePost', () => {
    const donePosts = postListManager.getActualDonePostList()
    if (donePosts.length === 0) {
      return $log.warn(`${sign}#openSamplePost: gallery has not "done" posts at that moment!`)
    }

    const isImage = (post) => post.preview.info.isSomeImage
    const imagePosts = donePosts.filter(isImage)
    const samplePost = imagePosts.length !== 0
      ? sample(imagePosts)
      : sample(donePosts)

    return $ctrl.openPost(samplePost)
  })

  $ctrl.wrapM('openSharedPost',
    stayInListQueue.make((post, slideIndex) => {
      if (!$ctrl.ggsBootstrap.properties.modals_use) return $log.warn(`${sign}#openSharedPost: "modals_use" is disabled - skip!`)

      return $ctrl.isAllModalsClosed().then((closed) => {
        if (!closed) return $log.warn(`${sign}#openSharedPost: some modals are not closed - skip!`)
        return extOne.open(post, slideIndex)
      })
    }))

  // rpc
  const rpcGetTopPosts =
    (topPostsMaxId, topPostsLimit) =>
      galleryRpc.getTopPosts(
        $ctrl.ggsBootstrap.properties.key,
        $ctrl.ggsBootstrap.properties.order_type,
        $ctrl.ggsBootstrap.properties.modulus,
        topPostsMaxId,
        topPostsLimit)

  const rpcGetAbovePosts =
    (abovePostsMaxId, abovePostsLimit) =>
      galleryRpc.getAbovePosts(
        $ctrl.ggsBootstrap.properties.key,
        $ctrl.ggsBootstrap.properties.order_type,
        abovePostsMaxId,
        abovePostsLimit)

  const rpcGetBelowPosts =
    (belowPostsMax, belowPostsSkip, belowPostsLimit) =>
      galleryRpc.getBelowPosts(
        $ctrl.ggsBootstrap.properties.key,
        $ctrl.ggsBootstrap.properties.order_type,
        $ctrl.ggsBootstrap.properties.modulus,
        belowPostsMax,
        belowPostsSkip,
        belowPostsLimit)

  // formatPost
  const formatPost = FormatPost($ctrl.ggsBootstrap.properties)

  // can fetch
  const mainCanFetchPosts = $ctrl.ggsBootstrap.properties.key2get.negate.show_load_more_button
  const canFetchAbovePosts = $ctrl.ggsBootstrap.properties.key2get.allow_fetch_above_posts
  const canFetchBelowPosts = stubTrue

  // managers
  const postListManager = PostListManager(
    $ctrl.ggsBootstrap.properties,
    $ctrl.ggsBootstrap.top_posts,
    rpcGetAbovePosts,
    rpcGetBelowPosts,
    stayInPostQueue,
    formatPost,
    mainCanFetchPosts,
    canFetchAbovePosts,
    canFetchBelowPosts)

  const orderTypeManager = OrderTypeManager(
    $ctrl.ggsBootstrap.properties,
    rpcGetTopPosts,
    extFlow,
    postListManager,
    stayInPostQueue,
    stayInListQueue,
    $ctrl.isAllModalsClosed)

  const stateManager = StateManager(
    $ctrl.ggsBootstrap.properties,
    $ctrl.postVoteEmitter,
    $ctrl.getters.isFlowInViewport,
    $ctrl.isFlowInViewportEmitter)

  const readyStateManager = ReadyStateManager(
    $ctrl.ggsBootstrap.properties,
    $ctrl.postFlagEmitter,
    mainCanFetchPosts,
    postListManager)

  const embedManager = EmbedManager(
    $ctrl.ggsBootstrap.properties,
    extFlow,
    $ctrl.postVoteEmitter,
    $ctrl.openSamplePost,
    $ctrl.closeModals,
    $ctrl.getters.headerHeight,
    $ctrl.headerHeightEmitter,
    readyStateManager)

  const modalsManager = ModalsManager(
    extFlow,
    postListManager)

  const presentationManager = PresentationManager(
    $ctrl.ggsBootstrap.properties,
    extOne,
    extList,
    extCampaign,
    postListManager,
    $ctrl.openSamplePost)

  const overridesManager = OverridesManager($ctrl.ggsBootstrap.properties)
  const attributesManager = AttributesManager(readyStateManager)

  const instantModeManager = InstantModeManager(
    $ctrl.ggsBootstrap.properties,
    extFlow,
    readyStateManager)

  // styleHandlers
  const styleHandlers = GalleryStyleHandlers(extFlow)

  // postList
  $ctrl.postList = postListManager.getPostList()

  // stylerNew
  $ctrl.stylerNew = GalleryStylerNew($ctrl.ggsBootstrap.properties)

  // sorting
  $ctrl.orderOptions = orderTypeManager.orderOptions
  $ctrl.changeOrderType = orderTypeManager.changeOrderType

  // init area
  $ctrl.wrapM('setupGPLM',
    qh.makeOver(
      qh.makeIf(
        env.getters.isIframeMode,
        WaitOnLoad),
      postListManager.init))

  $ctrl.wrapM('attachToStyler',
    qh.makeOver(
      $ctrl.stylerNew.init,
      qh.makeCatch(
        wrap(styleHandlers, $ctrl.stylerNew.reg),
        wrap(`${sign}#attachToStyler: error halted:`, $log.error))))

  $ctrl.wrapM('attachToEmitters', () => {
    entryEmitter.on($ctrl.entryCompleteHandler)
    readyStateManager.readyEmitter.on($ctrl.ggsMainReadyCallback)
  })

  $ctrl.wrapM('setupSharedPost', () => {
    const { displayed_item } = $ctrl.ggsBootstrap
    if (!displayed_item) {
      return
    }

    postListManager.assignActualRevision(displayed_item)
    return formatPost.below(displayed_item).then(qh.tap((post) => {
      const thumbs = post.media_list.filter((media) => !media.info.isSomeText).map((media) => media.file_thumb)
      return $q.all(thumbs.map(qh.makeAttempt(imgQueue.stayInQueue)))
    })).then((post) => {
      return $ctrl.openSharedPost(postFlag.done(post), $ctrl.ggsBootstrap.properties.shared_slide_index)
    }).catch((err) => {
      $log.warn(`${sign}#setupSharedPost: error halted:`, err)
    })
  })

  $ctrl.wrapH('$onInit',
    qh.makeJoin(
      $ctrl.setupGPLM,
      $ctrl.attachToStyler,
      $ctrl.attachToEmitters,
      $ctrl.setupSharedPost,
      stateManager.init,
      embedManager.init,
      modalsManager.init,
      overridesManager.init,
      orderTypeManager.init,
      readyStateManager.init,
      attributesManager.init,
      instantModeManager.init,
      presentationManager.init))

  // destroy area
  $ctrl.wrapM('detachFromStyler', () => {
    return qh.join(
      $ctrl.stylerNew.release(styleHandlers),
      $ctrl.stylerNew.destroy())
  })

  $ctrl.wrapM('detachFromEmitters', () => {
    entryEmitter.off($ctrl.entryCompleteHandler)
    readyStateManager.readyEmitter.off($ctrl.ggsMainReadyCallback)
  })

  $ctrl.wrapH('$onDestroy',
    qh.makeJoin(
      $ctrl.detachFromStyler,
      $ctrl.detachFromEmitters,
      stateManager.destroy,
      embedManager.destroy,
      modalsManager.destroy,
      postListManager.destroy,
      overridesManager.destroy,
      orderTypeManager.destroy,
      readyStateManager.destroy,
      attributesManager.destroy,
      instantModeManager.destroy,
      presentationManager.destroy))

  // emitters handlers
  $ctrl.wrapH('entryCompleteHandler', postListManager.resetAboveFetchLoop)

  // <ggs-preview-flow/> position handlers
  $ctrl.wrapH('flowInViewportHandler',
    qh.makeOver(
      $ctrl.setters.isFlowInViewport,
      $ctrl.isFlowInViewportEmitter.emit))

  // <ggs-preview-flow/> main handlers
  $ctrl.canShowPreview = (post) => {
    if (!postListManager.isCurrentLimitReached()) {
      return true
    }

    if (!mainCanFetchPosts()) {
      notification.debug(`Post ${post.id} will be denied!`)
      $log.warn(`${sign}#canShowPreview: post [post.id=${post.id}] is denied!`)
      return false
    }

    if (postPosition.isBelow(post)) {
      notification.debug(`Below post ${post.id} will be denied!`)
      $log.warn(`${sign}#canShowPreview: below [post.id=${post.id}] is denied!`)
      return false
    }

    const lastPost = postListManager.getLastActualDonePost()
    if (isPlainObject(lastPost)) {
      extFlow.remove(lastPost)
      postFlag.forceDeny(lastPost)
      $log.info(`${sign}#canShowPreview: above [post.id=${post.id}] will replace [lastPost.id=${lastPost.id}]!`)
      return true
    } else {
      notification.debug(`Above post ${post.id} will be denied!`)
      $log.warn(`${sign}#canShowPreview: above [post.id=${post.id}] will be denied!`)
      return false
    }
  }

  $ctrl.wrapH('previewDoneHandler',
    (post) => {
      // update post flags
      postFlag.done(post)
      postViewIndex.index(post)
      // deal with queue
      postListManager.delFromUnprocessedLists(post)
      // notify subscribers
      $ctrl.postFlagEmitter.emit()
    })

  $ctrl.wrapH('previewFailHandler',
    (post) => {
      // update post flags
      postFlag.fail(post)
      postViewIndex.index(post)
      // deal with queue
      postListManager.delFromUnprocessedLists(post)
      // notify subscribers
      $ctrl.postFlagEmitter.emit()
    })

  $ctrl.wrapH('previewDenyHandler',
    (post) => {
      // update post flags
      postFlag.deny(post)
      postViewIndex.index(post)
      // deal with queue
      postListManager.delFromUnprocessedLists(post)
      // notify subscribers
      $ctrl.postFlagEmitter.emit()
    })

  $ctrl.wrapH('previewSelectHandler', $ctrl.openPost)

  // <ggs-vote-frame/> handlers
  $ctrl.wrapH('voteChangeHandler',
    (post, isVoted, votesCount) => {
      post.votes_count = votesCount
      $ctrl.postVoteEmitter.emit(post, isVoted, votesCount)
    })

  $ctrl.wrapH('voteCountHideHandler', () => {
    $ctrl.ggsBootstrap.properties.override('show_vote_count', false)
  })

  $ctrl.wrapH('votingDisallowHandler', () => {
    $ctrl.ggsBootstrap.properties.override('allow_voting', false)
    $ctrl.ggsBootstrap.properties.override('show_vote_count', false)
  })

  // <ggs-gallery-load-more/> handler
  $ctrl.wrapH('loadMoreClickHandler',
    stayInListQueue.make(
      stayInPostQueue.make(
        qh.makeIf(
          qh.makeAnd(
            $ctrl.isAllModalsClosed,
            $ctrl.ggsBootstrap.properties.key2get.show_load_more_button),
          qh.makeOver(
            wrap(false, $ctrl.ggsBootstrap.properties.key2override.show_load_more_button),
            postListManager.delDeniedFromPostList,
            postListManager.resetAboveFetchLoop)))))

  // <ggs-gallery-modal-list/> handlers
  $ctrl.wrapH('modalDisplayedHandler',
    qh.makeIf(
      $ctrl.ggsBootstrap.properties.key2get.scroll_to_preview_on_modal_change,
      extFlow.scrollTo))

  $ctrl.getPrevModal = (actCurr) =>
    $ctrl.ggsBootstrap.properties.presentation_auto_control
      ? presentationManager.getPrevModal(actCurr)
      : modalsManager.getPrevModal(actCurr)

  $ctrl.getNextModal = (actCurr) =>
    $ctrl.ggsBootstrap.properties.presentation_auto_control
      ? presentationManager.getNextModal(actCurr)
      : modalsManager.getNextModal(actCurr)

  // <ggs-gallery-campaign/>, <ggs-gallery-modal-list/>, and <ggs-gallery-modal-one/> handlers
  $ctrl.wrapH('popupOpenHandler', attributesManager.reportPopupOpen)
  $ctrl.wrapH('popupClosedHandler', attributesManager.reportPopupClosed)
}
